import api from '@/shared/utils/apiConfig';
import i18n from '@/shared/i18n';

export const TIME_OFF_GRANT_EMPLOYEE_TYPES = ['full_time', 'part_time', 'intern'] as const;
export type TimeOffGrantEmployeeType = (typeof TIME_OFF_GRANT_EMPLOYEE_TYPES)[number];

export const TIME_OFF_GRANT_EMPLOYMENT_STAGES = ['probation', 'confirmed'] as const;
export type TimeOffGrantEmploymentStage = (typeof TIME_OFF_GRANT_EMPLOYMENT_STAGES)[number];

export const TIME_OFF_GRANT_CADENCES = ['remaining_year', 'monthly'] as const;
export type TimeOffGrantCadence = (typeof TIME_OFF_GRANT_CADENCES)[number];

export const TIME_OFF_GRANT_ACCRUAL_FROMS = ['confirmation', 'join_date'] as const;
export type TimeOffGrantAccrualFrom = (typeof TIME_OFF_GRANT_ACCRUAL_FROMS)[number];

export type TimeOffTypeStatus = 'active' | 'inactive';
export type TimeOffQuotaMode = 'yearly' | 'per_event' | 'none';

export interface TimeOffTypeRecord {
  id: string;
  code: string;
  name: string;
  description?: string;
  color?: string;
  status: TimeOffTypeStatus;
  quotaMode: TimeOffQuotaMode;
  tracksBalance: boolean;
  isPaid: boolean;
  isSocialInsurance: boolean;
  defaultAllocatedDays?: number;
  maxDaysPerRequest?: number;
  autoGrant: boolean;
  grantCadence: TimeOffGrantCadence | null;
  grantAccrualFrom: TimeOffGrantAccrualFrom | null;
  grantEmployeeTypes: string[];
  grantEmploymentStages: string[];
  createdBy?: { id: string; name: string } | null;
  updatedBy?: { id: string; name: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTimeOffTypePayload {
  code: string;
  name: string;
  description?: string;
  color?: string;
  quotaMode?: TimeOffQuotaMode;
  tracksBalance?: boolean;
  isPaid?: boolean;
  isSocialInsurance?: boolean;
  defaultAllocatedDays?: number | null;
  maxDaysPerRequest?: number | null;
  autoGrant?: boolean;
  grantCadence?: TimeOffGrantCadence | null;
  grantAccrualFrom?: TimeOffGrantAccrualFrom | null;
  grantEmployeeTypes?: string[];
  grantEmploymentStages?: string[];
}

export interface UpdateTimeOffTypePayload {
  code?: string;
  name?: string;
  description?: string;
  color?: string;
  quotaMode?: TimeOffQuotaMode;
  tracksBalance?: boolean;
  isPaid?: boolean;
  isSocialInsurance?: boolean;
  defaultAllocatedDays?: number | null;
  maxDaysPerRequest?: number | null;
  autoGrant?: boolean;
  grantCadence?: TimeOffGrantCadence | null;
  grantAccrualFrom?: TimeOffGrantAccrualFrom | null;
  grantEmployeeTypes?: string[];
  grantEmploymentStages?: string[];
}

export const resolveTimeOffQuotaMode = (type: {
  quotaMode?: TimeOffQuotaMode | string;
  tracksBalance?: boolean;
}): TimeOffQuotaMode => {
  if (type.quotaMode === 'yearly' || type.quotaMode === 'per_event' || type.quotaMode === 'none') {
    return type.quotaMode;
  }
  return type.tracksBalance === false ? 'none' : 'yearly';
};

export interface ListTimeOffTypesParams {
  status?: TimeOffTypeStatus;
  search?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface ListTimeOffTypesResponse {
  data: TimeOffTypeRecord[];
  total: number;
  page: number;
  pages: number;
}

export const listTimeOffTypes = async (
  params: ListTimeOffTypesParams = {}
): Promise<ListTimeOffTypesResponse> => {
  const response = await api.get('/scheduling/time-off-types', { params });
  const data: TimeOffTypeRecord[] = response.data.data ?? [];
  return {
    data,
    total: response.data.total ?? data.length,
    page: response.data.page ?? params.page ?? 1,
    pages: response.data.pages ?? 1,
  };
};

export const createTimeOffType = async (
  payload: CreateTimeOffTypePayload
): Promise<TimeOffTypeRecord> => {
  const response = await api.post('/scheduling/time-off-types', payload);
  return response.data.data;
};

export const updateTimeOffType = async (
  id: string,
  payload: UpdateTimeOffTypePayload
): Promise<TimeOffTypeRecord> => {
  const response = await api.put(`/scheduling/time-off-types/${id}`, payload);
  return response.data.data;
};

export const updateTimeOffTypeStatus = async (
  id: string,
  status: TimeOffTypeStatus
): Promise<TimeOffTypeRecord> => {
  const response = await api.patch(`/scheduling/time-off-types/${id}/status`, { status });
  return response.data.data;
};

export const TIME_OFF_GRANT_ACCRUAL_FROM_LABELS: Record<TimeOffGrantAccrualFrom, string> = {
  get confirmation() { return i18n.t('scheduling:grantAccrualFrom.confirmation'); },
  get join_date() { return i18n.t('scheduling:grantAccrualFrom.joinDate'); },
};

export const TIME_OFF_GRANT_CADENCE_LABELS: Record<TimeOffGrantCadence, string> = {
  get remaining_year() { return i18n.t('scheduling:grantCadence.remainingYear'); },
  get monthly() { return i18n.t('scheduling:grantCadence.monthly'); },
};

export const TIME_OFF_TYPE_STATUS_LABELS: Record<TimeOffTypeStatus, string> = {
  get active() { return i18n.t('scheduling:entityStatus.active'); },
  get inactive() { return i18n.t('scheduling:entityStatus.inactive'); },
};

export const TIME_OFF_QUOTA_MODE_LABELS: Record<TimeOffQuotaMode, string> = {
  get yearly() { return i18n.t('scheduling:quotaMode.yearly'); },
  get per_event() { return i18n.t('scheduling:quotaMode.perEvent'); },
  get none() { return i18n.t('scheduling:quotaMode.none'); },
};
